import React, { useMemo } from 'react';
import { Phone, PhoneIncoming, PhoneOff, Clock, CheckCircle, Calendar, TrendingUp, TrendingDown } from 'lucide-react';
import { useDailyCallCenterHealthSummary } from '../hooks/useReports';
import DataTable from '../components/DataTable';
import QueryInfoButton from '../components/QueryInfoButton';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from 'recharts';
import { CALL_CENTER_QUERIES } from '../constants/queries';
import { formatChartDate, formatDate } from '../utils/formatters';

export default function DailyCallCenterHealthSummary() {
  const { data, isLoading, isError, error } = useDailyCallCenterHealthSummary();

  const chartData = useMemo(() => {
    if (!data?.data || data.data.length === 0) return [];

    return data.data
      .slice(0, 30) // Last 30 days
      .reverse() // Show oldest to newest
      .map((row) => ({
        date: formatChartDate(row.call_date),
        totalInteractions: parseInt(row.total_interactions) || 0,
        answeredCalls: parseInt(row.answered_calls) || 0,
        abandonedCalls: parseInt(row.abandoned_calls) || 0,
        sla30sPct: parseFloat(row.sla_30s_pct) || 0,
        avgQueueTime: parseFloat(row.avg_queue_time_sec) || 0,
        avgHandleTime: parseFloat(row.avg_handle_time_sec) || 0,
      }));
  }, [data]);

  // Calculate today's vs yesterday's comparison
  const comparison = useMemo(() => {
    if (!data?.data || data.data.length < 2) return null;

    const today = data.data[0];
    const yesterday = data.data[1];

    const calcChange = (todayVal, yesterdayVal) => {
      const t = parseFloat(todayVal) || 0;
      const y = parseFloat(yesterdayVal) || 0;
      if (y === 0) return 0;
      return ((t - y) / y * 100).toFixed(1);
    };

    return {
      interactions: calcChange(today.total_interactions, yesterday.total_interactions),
      answered: calcChange(today.answered_calls, yesterday.answered_calls),
      abandoned: calcChange(today.abandoned_calls, yesterday.abandoned_calls),
      sla: calcChange(today.sla_30s_pct, yesterday.sla_30s_pct),
    };
  }, [data]);

  const columns = [
    {
      key: 'call_date',
      label: 'Date',
      sortable: true,
      render: (value) => formatDate(value),
    },
    { key: 'total_interactions', label: 'Total Interactions', sortable: true, format: 'number' },
    { key: 'answered_calls', label: 'Answered', sortable: true, format: 'number' },
    { key: 'abandoned_calls', label: 'Abandoned', sortable: true, format: 'number' },
    {
      key: 'avg_queue_time_sec',
      label: 'Avg Queue (sec)',
      sortable: true,
      format: 'number',
      render: (value) => parseFloat(value || 0).toFixed(2),
    },
    {
      key: 'avg_handle_time_sec',
      label: 'Avg Handle (sec)',
      sortable: true,
      format: 'number',
      render: (value) => parseFloat(value || 0).toFixed(2),
    },
    {
      key: 'sla_30s_pct',
      label: 'SLA 30s %',
      sortable: true,
      render: (value) => {
        const pct = parseFloat(value || 0);
        const colorClass = pct >= 80 ? 'text-green-600' : pct >= 60 ? 'text-yellow-600' : 'text-red-600';
        return <span className={`font-medium ${colorClass}`}>{pct.toFixed(2)}%</span>;
      },
    },
  ];

  const StatCard = ({ title, value, icon: Icon, change, changeLabel, isPositiveGood = true }) => {
    const isPositive = parseFloat(change) > 0;
    const changeColor = isPositiveGood
      ? (isPositive ? 'text-green-600' : 'text-red-600')
      : (isPositive ? 'text-red-600' : 'text-green-600');

    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="p-2 bg-primary-50 rounded-lg">
            <Icon className="w-5 h-5 text-primary-600" />
          </div>
          {change !== null && change !== undefined && (
            <div className={`flex items-center gap-1 text-sm ${changeColor}`}>
              {isPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
              {Math.abs(parseFloat(change))}%
            </div>
          )}
        </div>
        <div className="mt-3">
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {changeLabel && <p className="text-xs text-gray-400 mt-1">{changeLabel}</p>}
        </div>
      </div>
    );
  };

  const todayData = data?.data?.[0];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Daily Call Center Health Summary</h2>
          <p className="text-gray-500 mt-1">
            Daily breakdown of call center performance metrics and SLA compliance
          </p>
        </div>
        <QueryInfoButton
          title="Call Center Health Data Sources"
          queries={[CALL_CENTER_QUERIES.dailyHealthSummary]}
        />
      </div>

      {/* Today's Summary Cards */}
      {todayData && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          <StatCard
            title="Total Interactions"
            value={parseInt(todayData.total_interactions || 0).toLocaleString()}
            icon={Phone}
            change={comparison?.interactions}
            changeLabel="vs yesterday"
          />
          <StatCard
            title="Answered Calls"
            value={parseInt(todayData.answered_calls || 0).toLocaleString()}
            icon={PhoneIncoming}
            change={comparison?.answered}
            changeLabel="vs yesterday"
          />
          <StatCard
            title="Abandoned Calls"
            value={parseInt(todayData.abandoned_calls || 0).toLocaleString()}
            icon={PhoneOff}
            change={comparison?.abandoned}
            changeLabel="vs yesterday"
            isPositiveGood={false}
          />
          <StatCard
            title="Avg Queue Time"
            value={`${parseFloat(todayData.avg_queue_time_sec || 0).toFixed(1)}s`}
            icon={Clock}
          />
          <StatCard
            title="Avg Handle Time"
            value={`${parseFloat(todayData.avg_handle_time_sec || 0).toFixed(1)}s`}
            icon={Clock}
          />
          <StatCard
            title="SLA 30s %"
            value={`${parseFloat(todayData.sla_30s_pct || 0).toFixed(1)}%`}
            icon={CheckCircle}
            change={comparison?.sla}
            changeLabel="vs yesterday"
          />
        </div>
      )}

      {/* Charts */}
      {chartData.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Call Volume Chart */}
          <div className="card">
            <h3 className="card-header flex items-center gap-2">
              <Phone className="w-5 h-5 text-primary-600" />
              Call Volume Trend (Last 30 Days)
            </h3>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickLine={false} />
                  <YAxis tick={{ fontSize: 12 }} tickLine={false} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                    }}
                  />
                  <Legend />
                  <Bar dataKey="answeredCalls" name="Answered" fill="#22c55e" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="abandonedCalls" name="Abandoned" fill="#ef4444" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* SLA Trend Chart */}
          <div className="card">
            <h3 className="card-header flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-primary-600" />
              SLA Compliance Trend (Last 30 Days)
            </h3>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} tickLine={false} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                    }}
                    formatter={(value) => [`${value.toFixed(1)}%`, 'SLA 30s']}
                  />
                  <Line
                    type="monotone"
                    dataKey="sla30sPct"
                    name="SLA 30s %"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                  {/* Target line at 80% */}
                  <Line
                    type="monotone"
                    dataKey={() => 80}
                    name="Target (80%)"
                    stroke="#f59e0b"
                    strokeDasharray="5 5"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Data Table */}
      <div className="card">
        <h3 className="card-header flex items-center gap-2">
          <Calendar className="w-5 h-5 text-primary-600" />
          Daily Metrics Data
        </h3>
        <DataTable
          data={data?.data}
          columns={columns}
          loading={isLoading}
          emptyMessage="No call center data available"
        />
      </div>
    </div>
  );
}
